import type { CSSProperties, ReactNode } from 'react'
import type { VendorDetailItem } from '@/services/vendorDetails'
import type { FieldDefinition } from '@/tableControls'

export const VENDOR_DETAILS_PAGE_KEY = 'vendor-details'
export const VENDOR_DETAILS_VISIBLE_COLUMNS_KEY = 'vendor_details_visible_columns'

export const REFRESH_TITLE =
  'Syncs vendor records from QuickBooks (contact info, balance, payment terms, 1099 flag, notes, website, Payment Method, Overseen By, Liability Timing custom fields, and more), then recomputes Gross Spend LTM and Expense Transaction counts from QBO bills, purchases, and vendor credits.'

export const FAIRE_COLUMN_TITLE =
  'Yes when "faire" appears in the vendor\'s Notes or Website field in QuickBooks.'

export const GROSS_SPEND_LTM_TITLE =
  'Sum of bill amounts plus Purchase transactions (QBO Expense, check, and credit card spend) minus vendor credits over the LTM window. Includes open bills. Bill payments are excluded so paid bills are not double-counted.'

export const COLUMN_LABELS = {
  display_name: 'Vendor',
  active: 'Status',
  is_faire_vendor: 'Faire',
  balance: 'Balance',
  gross_spend_ltm: 'Gross spend, LTM',
  expense_txn_count_ltm: 'Expense txns, LTM',
  payment_method: 'Payment method',
  liability_timing: 'Liability timing',
  term_ref: 'Payment terms',
  overseen_by: 'Overseen by',
  vendor_1099: '1099',
  primary_email: 'Email',
  primary_phone: 'Phone',
  print_on_check_name: 'Print on check',
  notes: 'Notes',
  tax_identifier: 'Tax ID',
  acct_num: 'Acct #',
  mobile: 'Mobile',
  fax: 'Fax',
  web_url: 'Website',
  address: 'Address',
} as const

export type VendorColumnKey = keyof typeof COLUMN_LABELS

export interface VendorColumnDef {
  key: VendorColumnKey
  label: (typeof COLUMN_LABELS)[VendorColumnKey]
  sortKey: keyof VendorDetailItem | string
  title?: string
  align?: 'left' | 'right'
  required?: boolean
}

export const VENDOR_COLUMNS: VendorColumnDef[] = [
  { key: 'display_name', label: COLUMN_LABELS.display_name, sortKey: 'display_name', required: true },
  { key: 'active', label: COLUMN_LABELS.active, sortKey: 'active' },
  { key: 'is_faire_vendor', label: COLUMN_LABELS.is_faire_vendor, sortKey: 'is_faire_vendor', title: FAIRE_COLUMN_TITLE },
  { key: 'balance', label: COLUMN_LABELS.balance, sortKey: 'balance', align: 'right' },
  { key: 'gross_spend_ltm', label: COLUMN_LABELS.gross_spend_ltm, sortKey: 'gross_spend_ltm', align: 'right', title: GROSS_SPEND_LTM_TITLE },
  { key: 'expense_txn_count_ltm', label: COLUMN_LABELS.expense_txn_count_ltm, sortKey: 'expense_txn_count_ltm', align: 'right' },
  { key: 'payment_method', label: COLUMN_LABELS.payment_method, sortKey: 'payment_method' },
  { key: 'liability_timing', label: COLUMN_LABELS.liability_timing, sortKey: 'liability_timing' },
  { key: 'term_ref', label: COLUMN_LABELS.term_ref, sortKey: 'term_ref' },
  { key: 'overseen_by', label: COLUMN_LABELS.overseen_by, sortKey: 'overseen_by' },
  { key: 'vendor_1099', label: COLUMN_LABELS.vendor_1099, sortKey: 'vendor_1099' },
  { key: 'primary_email', label: COLUMN_LABELS.primary_email, sortKey: 'primary_email' },
  { key: 'primary_phone', label: COLUMN_LABELS.primary_phone, sortKey: 'primary_phone' },
  { key: 'print_on_check_name', label: COLUMN_LABELS.print_on_check_name, sortKey: 'print_on_check_name' },
  { key: 'notes', label: COLUMN_LABELS.notes, sortKey: 'notes' },
  { key: 'tax_identifier', label: COLUMN_LABELS.tax_identifier, sortKey: 'tax_identifier' },
  { key: 'acct_num', label: COLUMN_LABELS.acct_num, sortKey: 'acct_num' },
  { key: 'mobile', label: COLUMN_LABELS.mobile, sortKey: 'mobile' },
  { key: 'fax', label: COLUMN_LABELS.fax, sortKey: 'fax' },
  { key: 'web_url', label: COLUMN_LABELS.web_url, sortKey: 'web_url' },
  { key: 'address', label: COLUMN_LABELS.address, sortKey: 'bill_addr_line1' },
]

export const DEFAULT_VISIBLE_COLUMNS: VendorColumnKey[] = VENDOR_COLUMNS.map((col) => col.key)

const VENDOR_FIELD_TYPES: Partial<Record<VendorColumnKey, FieldDefinition['type']>> = {
  active: 'boolean',
  is_faire_vendor: 'boolean',
  vendor_1099: 'boolean',
  balance: 'number',
  gross_spend_ltm: 'number',
  expense_txn_count_ltm: 'number',
}

export const VENDOR_FIELD_DEFINITIONS: FieldDefinition[] = VENDOR_COLUMNS.map((col) => ({
  key: col.key,
  label: col.label,
  type: VENDOR_FIELD_TYPES[col.key] ?? 'text',
  locked: col.required,
  defaultVisible: DEFAULT_VISIBLE_COLUMNS.includes(col.key),
  sortable: true,
  ...(col.key === 'address'
    ? { accessor: (row: unknown) => formatAddress(row as VendorDetailItem) }
    : {}),
}))

/** Min width so uppercase header label + sort icon + padding fits without clipping. */
export function headerMinWidth(label: string): number {
  const textWidth = label.toUpperCase().length * 7.7
  const chromePx = 64
  return Math.ceil(textWidth + chromePx)
}

export const DEFAULT_WIDTHS: Record<string, number> = {
  ...Object.fromEntries(
    Object.entries(COLUMN_LABELS).map(([key, label]) => [key, headerMinWidth(label)])
  ),
  display_name: 300,
}

/** Accounting format: $#,##0.00;($#,##0.00);- */
export function formatAmount(value: number | string): string {
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  if (!Number.isFinite(n)) return String(value)
  if (n === 0) return '-'
  const formatted = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return n < 0 ? `($${formatted})` : `$${formatted}`
}

export function formatExpenseTxnCount(value: number): string {
  return value === 0 ? '-' : String(value)
}

export function formatAddress(vendor: VendorDetailItem): string {
  return [
    vendor.bill_addr_line1,
    vendor.bill_addr_line2,
    vendor.bill_addr_line3,
    vendor.bill_addr_city,
    vendor.bill_addr_state,
    vendor.bill_addr_postal_code,
    vendor.bill_addr_country,
  ]
    .filter(Boolean)
    .join(', ')
}

function YesNoBadge({ value }: { value: boolean }) {
  return value ? (
    <span className="badge badge-info">Yes</span>
  ) : (
    <span className="badge badge-neutral">No</span>
  )
}

function ReadOnlyCell({
  value,
  columnKey,
  cellStyle,
  className = '',
}: {
  value: string
  columnKey: string
  cellStyle: (key: string) => CSSProperties
  className?: string
}) {
  return (
    <td
      className={`truncate ${className}`}
      style={cellStyle(columnKey)}
      title={value || undefined}
    >
      {value || '—'}
    </td>
  )
}

export function renderVendorTableCell(
  vendor: VendorDetailItem,
  columnKey: VendorColumnKey,
  cellStyle: (key: string) => CSSProperties,
  cellClassName = ''
): ReactNode {
  switch (columnKey) {
    case 'display_name':
      return (
        <td className={cellClassName || undefined} style={cellStyle('display_name')}>
          {vendor.url ? (
            <a
              href={vendor.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate font-medium text-brand-700 hover:underline"
              title={vendor.display_name}
              onClick={(e) => e.stopPropagation()}
            >
              {vendor.display_name}
            </a>
          ) : (
            <div className="truncate font-medium text-gray-900" title={vendor.display_name}>
              {vendor.display_name}
            </div>
          )}
          {vendor.company_name && vendor.company_name !== vendor.display_name ? (
            <div className="truncate text-xs text-gray-500" title={vendor.company_name}>
              {vendor.company_name}
            </div>
          ) : null}
        </td>
      )
    case 'active':
      return (
        <td style={cellStyle('active')}>
          {vendor.active ? (
            <span className="badge badge-info">Active</span>
          ) : (
            <span className="badge badge-neutral">Inactive</span>
          )}
        </td>
      )
    case 'is_faire_vendor':
      return (
        <td style={cellStyle('is_faire_vendor')}>
          <YesNoBadge value={vendor.is_faire_vendor} />
        </td>
      )
    case 'balance':
      return (
        <td className="tabular-nums text-right" style={cellStyle('balance')}>
          {formatAmount(vendor.balance)}
        </td>
      )
    case 'gross_spend_ltm':
      return (
        <td className="tabular-nums text-right" style={cellStyle('gross_spend_ltm')}>
          {formatAmount(vendor.gross_spend_ltm)}
        </td>
      )
    case 'expense_txn_count_ltm':
      return (
        <td className="tabular-nums text-right" style={cellStyle('expense_txn_count_ltm')}>
          {formatExpenseTxnCount(vendor.expense_txn_count_ltm)}
        </td>
      )
    case 'vendor_1099':
      return (
        <td style={cellStyle('vendor_1099')}>
          <YesNoBadge value={vendor.vendor_1099} />
        </td>
      )
    case 'web_url':
      return (
        <td className="truncate" style={cellStyle('web_url')} title={vendor.web_url}>
          {vendor.web_url ? (
            <a
              href={
                vendor.web_url.startsWith('http') ? vendor.web_url : `https://${vendor.web_url}`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-700 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {vendor.web_url}
            </a>
          ) : (
            '—'
          )}
        </td>
      )
    case 'address': {
      const address = formatAddress(vendor)
      return (
        <td className="truncate" style={cellStyle('address')} title={address}>
          {address || '—'}
        </td>
      )
    }
    case 'payment_method':
      return (
        <ReadOnlyCell value={vendor.payment_method} columnKey="payment_method" cellStyle={cellStyle} />
      )
    case 'liability_timing':
      return (
        <ReadOnlyCell value={vendor.liability_timing} columnKey="liability_timing" cellStyle={cellStyle} />
      )
    case 'term_ref':
      return <ReadOnlyCell value={vendor.term_ref} columnKey="term_ref" cellStyle={cellStyle} />
    case 'overseen_by':
      return <ReadOnlyCell value={vendor.overseen_by} columnKey="overseen_by" cellStyle={cellStyle} />
    case 'primary_email':
      return (
        <ReadOnlyCell value={vendor.primary_email} columnKey="primary_email" cellStyle={cellStyle} />
      )
    case 'primary_phone':
      return (
        <ReadOnlyCell value={vendor.primary_phone} columnKey="primary_phone" cellStyle={cellStyle} />
      )
    case 'print_on_check_name':
      return (
        <ReadOnlyCell
          value={vendor.print_on_check_name}
          columnKey="print_on_check_name"
          cellStyle={cellStyle}
        />
      )
    case 'notes':
      return <ReadOnlyCell value={vendor.notes} columnKey="notes" cellStyle={cellStyle} />
    case 'tax_identifier':
      return (
        <ReadOnlyCell value={vendor.tax_identifier} columnKey="tax_identifier" cellStyle={cellStyle} />
      )
    case 'acct_num':
      return <ReadOnlyCell value={vendor.acct_num} columnKey="acct_num" cellStyle={cellStyle} />
    case 'mobile':
      return <ReadOnlyCell value={vendor.mobile} columnKey="mobile" cellStyle={cellStyle} />
    case 'fax':
      return <ReadOnlyCell value={vendor.fax} columnKey="fax" cellStyle={cellStyle} />
    default:
      return null
  }
}

export function parseVisibleColumns(raw: string | undefined): VendorColumnKey[] {
  if (!raw) return DEFAULT_VISIBLE_COLUMNS
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE_COLUMNS
    const valid = new Set(DEFAULT_VISIBLE_COLUMNS)
    const keys = parsed.filter(
      (key): key is VendorColumnKey => typeof key === 'string' && valid.has(key as VendorColumnKey)
    )
    if (!keys.includes('display_name')) {
      keys.unshift('display_name')
    }
    return keys.length > 0 ? keys : DEFAULT_VISIBLE_COLUMNS
  } catch {
    return DEFAULT_VISIBLE_COLUMNS
  }
}
